import styled, { css } from 'styled-components';

/**
 * POSButton - A themed button component for POS interfaces
 *
 * Variants:
 * - primary: Solid orange background (default)
 * - outlined: Orange border with transparent background
 * - secondary: White/gray button
 * - danger: Red button for destructive actions
 */
const POSButton = styled.button`
  padding: ${(props) => (props.size === 'large' ? '16px 24px' : '12px 20px')};
  border-radius: ${(props) => props.theme.borderRadius?.md || '8px'};
  font-size: ${(props) => (props.size === 'large' ? '15px' : '14px')};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: ${(props) => (props.fullWidth ? '100%' : 'auto')};

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Primary variant (default) */
  ${(props) =>
    (!props.variant || props.variant === 'primary') &&
    css`
      background: ${props.theme.colors?.primary || '#FA4616'};
      border: none;
      color: #ffffff;

      &:hover:not(:disabled) {
        background: ${props.theme.colors?.primaryHover || '#E03E0C'};
      }
    `}

  /* Outlined variant */
  ${(props) =>
    props.variant === 'outlined' &&
    css`
      background: transparent;
      border: 2px solid ${props.theme.colors?.primary || '#FA4616'};
      color: ${props.theme.colors?.primary || '#FA4616'};

      &:hover:not(:disabled) {
        background: rgba(250, 70, 22, 0.1);
      }
    `}

  /* Secondary variant */
  ${(props) =>
    props.variant === 'secondary' &&
    css`
      background: ${props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.1)'
        : '#f1f5f9'};
      border: 1px solid
        ${props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.2)'
          : 'rgba(0, 0, 0, 0.1)'};
      color: ${props.theme.name === 'dark'
        ? props.theme.colors?.textGray?.secondary || '#94A3B8'
        : '#64748B'};

      &:hover:not(:disabled) {
        border-color: ${props.theme.colors?.primary || '#FA4616'};
        color: ${props.theme.colors?.primary || '#FA4616'};
      }
    `}

  /* Danger variant */
  ${(props) =>
    props.variant === 'danger' &&
    css`
      background: transparent;
      border: 1px solid ${props.theme.colors?.error || '#EF4444'};
      color: ${props.theme.colors?.error || '#EF4444'};

      &:hover:not(:disabled) {
        background: ${props.theme.colors?.error || '#EF4444'};
        color: #ffffff;
      }
    `}
`;

/**
 * POSPaymentButton - Larger payment buttons for checkout
 */
export const POSPaymentButton = styled(POSButton)`
  padding: 16px;
  font-size: 15px;
  font-weight: 600;
  flex: 1;
`;

/**
 * POSIconButton - Small icon-only button
 */
export const POSIconButton = styled.button`
  width: 36px;
  height: 36px;
  border: none;
  background: ${(props) =>
    props.variant === 'ghost'
      ? 'transparent'
      : props.theme.colors?.primary || '#FA4616'};
  color: ${(props) =>
    props.variant === 'ghost'
      ? props.theme.colors?.textGray?.muted || '#64748B'
      : '#ffffff'};
  border-radius: 6px;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.variant === 'ghost'
        ? 'transparent'
        : props.theme.colors?.primaryHover || '#E03E0C'};
    color: ${(props) =>
      props.variant === 'ghost'
        ? props.theme.colors?.error || '#EF4444'
        : '#ffffff'};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export default POSButton;
